import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { generateDailyCombinedGrid, generatePracticeCombinedGrid, getTodayDateString } from '../utils/dailyPuzzleGenerator';
import { useAuth } from '../contexts/AuthContext';
import { saveGameResult, saveDailyChallengeResult, hasCompletedTodaysChallenge, getTimeUntilNextChallenge } from '../utils/gameResultsService';
import Whiteboard from './Whiteboard';

interface GridCell {
  value: number | null;
  isGiven: boolean;
  isValid: boolean;
}

interface CombinedPuzzle {
  grid: GridCell[][];
  rowSums: number[];
  colSums: number[];
  rowProducts: number[];
  colProducts: number[];
  solution: number[][];
}

// Generate combined puzzle (daily or practice mode)
const generateCombinedPuzzle = (isPracticeMode: boolean): CombinedPuzzle => {
  const dailyPuzzle = isPracticeMode ? generatePracticeCombinedGrid() : generateDailyCombinedGrid();
  
  // Convert to our PuzzleData format
  const grid: GridCell[][] = dailyPuzzle.grid.map((row, r) =>
    row.map((cell, c) => ({
      value: cell,
      isGiven: dailyPuzzle.givenCells.some(given => given.row === r && given.col === c),
      isValid: true
    }))
  );
  
  return {
    grid,
    rowSums: dailyPuzzle.rowSums,
    colSums: dailyPuzzle.colSums,
    rowProducts: dailyPuzzle.rowProducts,
    colProducts: dailyPuzzle.colProducts,
    solution: dailyPuzzle.solution
  };
};

const CombinedGrid = () => {
  const { isAuthenticated, user } = useAuth();
  const [searchParams] = useSearchParams();
  const isPracticeMode = searchParams.get('mode') === 'practice';
  
  const [puzzle, setPuzzle] = useState<CombinedPuzzle | null>(null);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [selectedCell, setSelectedCell] = useState<{row: number, col: number} | null>(null);
  const [gameComplete, setGameComplete] = useState(false);
  const [startTime, setStartTime] = useState(Date.now());
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [feedback, setFeedback] = useState<'correct' | 'incorrect' | null>(null);
  const [showHint, setShowHint] = useState(false);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [completionTime, setCompletionTime] = useState(0);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [feedbackTimeout, setFeedbackTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showWhiteboard, setShowWhiteboard] = useState(false);
  const [hasCompletedToday, setHasCompletedToday] = useState(false);
  const [timeUntilNext, setTimeUntilNext] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [checkingCompletion, setCheckingCompletion] = useState(true);
  const maxHints = 3;

  // Check if user has completed today's challenge
  useEffect(() => {
    const checkCompletion = async () => {
      if (!isPracticeMode && isAuthenticated && user) {
        setCheckingCompletion(true);
        const completed = await hasCompletedTodaysChallenge(user.id, 'combined_grid');
        setHasCompletedToday(completed);
        setCheckingCompletion(false);
      } else {
        setCheckingCompletion(false);
      }
    };

    checkCompletion();
  }, [isPracticeMode, isAuthenticated, user]);

  // Countdown timer for next challenge
  useEffect(() => {
    if (!hasCompletedToday || isPracticeMode) return;

    const updateCountdown = () => {
      setTimeUntilNext(getTimeUntilNextChallenge());
    };

    updateCountdown(); // Initial update
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [hasCompletedToday, isPracticeMode]);

  // Initialize puzzle
  useEffect(() => {
    if (!hasCompletedToday || isPracticeMode) {
      setPuzzle(generateCombinedPuzzle(isPracticeMode));
    }
  }, [hasCompletedToday, isPracticeMode]);


  // Timer effect
  useEffect(() => {
    if (gameComplete) return;
    
    const interval = setInterval(() => {
      setTimeElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime, gameComplete]);

  // Get currently used numbers on the board
  const getUsedNumbers = (): Set<number> => {
    const usedNumbers = new Set<number>();
    if (!puzzle) return usedNumbers;

    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 3; col++) {
        const cell = puzzle.grid[row][col];
        if (cell.value !== null && cell.value >= 1 && cell.value <= 9) {
          usedNumbers.add(cell.value);
        }
      }
    }
    return usedNumbers;
  };

  // Check if the grid is completely filled
  const isGridComplete = (): boolean => {
    if (!puzzle) return false;
    
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 3; col++) {
        if (puzzle.grid[row][col].value === null) {
          return false;
        }
      }
    }
    return true;
  };

  // Handle cell click
  const handleCellClick = (row: number, col: number) => {
    if (!puzzle || puzzle.grid[row][col].isGiven) return;
    setSelectedCell({row, col});
  };

  // Handle number input
  const handleNumberInput = (num: number) => {
    if (!puzzle || !selectedCell) return;

    const selectedCellValue = puzzle.grid[selectedCell.row][selectedCell.col].value;
    const usedNumbers = getUsedNumbers();
    
    // If the number is already used and we're not removing it from the current cell, don't allow it
    if (usedNumbers.has(num) && selectedCellValue !== num) {
      return;
    }

    const newGrid = puzzle.grid.map((row, r) =>
      row.map((cell, c) => {
        if (r === selectedCell.row && c === selectedCell.col && !cell.isGiven) {
          return {
            ...cell,
            value: cell.value === num ? null : num,
            isValid: true
          };
        }
        return cell;
      })
    );

    setPuzzle({...puzzle, grid: newGrid});
    setFeedback(null);
    setHasSubmitted(false);
  };

  // Handle submit button
  const handleSubmit = () => {
    if (!puzzle || !isGridComplete()) return;
    
    setHasSubmitted(true);
    
    const newGrid = puzzle.grid.map((row, r) =>
      row.map((cell, c) => {
        if (cell.isGiven) {
          return cell;
        }
        
        const isCorrect = cell.value === puzzle.solution[r][c];
        return {
          ...cell,
          isValid: isCorrect
        };
      })
    );

    setPuzzle({...puzzle, grid: newGrid});
    
    // Check if puzzle is solved
    validatePuzzleCompletion({...puzzle, grid: newGrid});
    
    // Show feedback - check both cell validity AND constraint satisfaction
    const cellsValid = newGrid.every(row => 
      row.every(cell => cell.isGiven || cell.isValid)
    );
    
    // Check if all constraints are satisfied
    let constraintsSatisfied = true;
    if (cellsValid) {
      // Check if all cells are filled
      const allFilled = newGrid.every(row => row.every(cell => cell.value !== null));
      
      if (allFilled) {
        // Check row sums and products
        for (let row = 0; row < 3; row++) {
          const rowSum = newGrid[row].reduce((sum, cell) => sum + (cell.value || 0), 0);
          const rowProduct = newGrid[row].reduce((product, cell) => product * (cell.value || 0), 1);
          if (rowSum !== puzzle.rowSums[row] || rowProduct !== puzzle.rowProducts[row]) {
            constraintsSatisfied = false;
            break;
          }
        }
        
        // Check column sums and products
        if (constraintsSatisfied) {
          for (let col = 0; col < 3; col++) {
            const colSum = newGrid.reduce((sum, row) => sum + (row[col].value || 0), 0);
            const colProduct = newGrid.reduce((product, row) => product * (row[col].value || 0), 1);
            if (colSum !== puzzle.colSums[col] || colProduct !== puzzle.colProducts[col]) {
              constraintsSatisfied = false;
              break;
            }
          }
        }
      } else {
        constraintsSatisfied = false;
      }
    } else {
      constraintsSatisfied = false;
    }
    
    if (cellsValid && constraintsSatisfied) {
      setFeedback('correct');
    } else {
      setFeedback('incorrect');
    }
    
    const timeout = setTimeout(() => setFeedback(null), 2000);
    setFeedbackTimeout(timeout);
  };

  // Validate puzzle completion
  const validatePuzzleCompletion = (currentPuzzle: CombinedPuzzle) => {
    let isComplete = true;
    const usedNumbers = new Set<number>();

    // Check if all cells are filled and collect used numbers
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 3; col++) {
        const cell = currentPuzzle.grid[row][col];
        if (cell.value === null) {
          isComplete = false;
        } else {
          if (usedNumbers.has(cell.value)) {
            currentPuzzle.grid[row][col].isValid = false;
          } else if (cell.value >= 1 && cell.value <= 9) {
            usedNumbers.add(cell.value);
            currentPuzzle.grid[row][col].isValid = true;
          } else {
            currentPuzzle.grid[row][col].isValid = false;
          }
        }
      }
    }

    // Check if we have exactly numbers 1-9
    const hasAllNumbers = usedNumbers.size === 9 && 
      Array.from({length: 9}, (_, i) => i + 1).every(n => usedNumbers.has(n));

    if (isComplete && hasAllNumbers) {
      // Check both sums and products for rows and columns
      let constraintsValid = true;
      
      // Check row sums and products
      for (let row = 0; row < 3; row++) {
        const rowSum = currentPuzzle.grid[row].reduce((sum, cell) => sum + (cell.value || 0), 0);
        const rowProduct = currentPuzzle.grid[row].reduce((product, cell) => product * (cell.value || 0), 1);
        if (rowSum !== currentPuzzle.rowSums[row] || rowProduct !== currentPuzzle.rowProducts[row]) {
          constraintsValid = false;
          break;
        }
      }

      // Check column sums and products
      if (constraintsValid) {
        for (let col = 0; col < 3; col++) {
          const colSum = currentPuzzle.grid.reduce((sum, row) => sum + (row[col].value || 0), 0);
          const colProduct = currentPuzzle.grid.reduce((product, row) => product * (row[col].value || 0), 1);
          if (colSum !== currentPuzzle.colSums[col] || colProduct !== currentPuzzle.colProducts[col]) {
            constraintsValid = false;
            break;
          }
        }
      }

      if (constraintsValid) {
        const finalTime = Math.floor((Date.now() - startTime) / 1000);
        setCompletionTime(finalTime);
        setGameComplete(true);

        // Save to database if user is authenticated
        if (isAuthenticated) {
          const accuracy = 100; // They completed the puzzle correctly
          
          // Save regular game session
          saveGameResult({
            user_id: user!.id,
            game_type: 'combined_grid',
            score: accuracy,
            completion_time_seconds: finalTime,
            hints_used: hintsUsed,
            accuracy: accuracy,
            completed: true,
            session_data: {
              puzzle_date: getTodayDateString(),
              final_time: finalTime,
              practice_mode: isPracticeMode
            }
          }).catch(error => {
            console.error('Failed to save combined grid result:', error);
          });

          // Also save daily challenge result if not in practice mode
          if (!isPracticeMode) {
            saveDailyChallengeResult(
              user!.id,
              'combined_grid',
              finalTime,
              hintsUsed,
              accuracy
            ).catch(error => {
              console.error('Failed to save daily challenge result:', error);
            });
          }
        }
      }
    }
  };

  // Handle hint button
  const handleGetHint = () => {
    if (!puzzle || hintsUsed >= maxHints) return;
    
    const emptyCells: {row: number, col: number}[] = [];
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 3; col++) {
        if (puzzle.grid[row][col].value === null && !puzzle.grid[row][col].isGiven) {
          emptyCells.push({row, col});
        }
      }
    }
    
    if (emptyCells.length === 0) return;
    
    const randomCell = emptyCells[Math.floor(Math.random() * emptyCells.length)];
    const newGrid = puzzle.grid.map((row, r) =>
      row.map((cell, c) => {
        if (r === randomCell.row && c === randomCell.col) {
          return {
            value: puzzle.solution[r][c],
            isGiven: true,
            isValid: true
          };
        }
        return cell;
      })
    );
    
    setPuzzle({...puzzle, grid: newGrid});
    setHintsUsed(prev => prev + 1);
    setHasSubmitted(false);
    setShowHint(true);
    setTimeout(() => setShowHint(false), 2000);
  };

  // Clear puzzle function
  const clearPuzzle = () => {
    if (!puzzle) return;
    
    // Check if there are any user-entered values
    const hasUserValues = puzzle.grid.some(row => 
      row.some(cell => !cell.isGiven && cell.value !== null)
    );
    
    if (hasUserValues) {
      const confirmed = window.confirm("Are you sure you want to clear the puzzle? This will remove all your entered numbers.");
      if (!confirmed) return;
    }
    
    // Clear all non-given cells
    const clearedGrid = puzzle.grid.map(row =>
      row.map(cell => ({
        ...cell,
        value: cell.isGiven ? cell.value : null,
        isValid: true
      }))
    );
    
    setPuzzle({...puzzle, grid: clearedGrid});
    setSelectedCell(null);
    setFeedback(null);
    setHasSubmitted(false);
    if (feedbackTimeout) {
      clearTimeout(feedbackTimeout);
      setFeedbackTimeout(null);
    }
  };

  // Start new puzzle
  const startNewPuzzle = () => {
    // If user is not authenticated, show signup prompt for unlimited puzzles
    if (!isAuthenticated) {
      setShowSignupPrompt(true);
      return;
    }

    if (feedbackTimeout) {
      clearTimeout(feedbackTimeout);
      setFeedbackTimeout(null);
    }
    
    setPuzzle(generateCombinedPuzzle(isPracticeMode));
    setGameComplete(false);
    setSelectedCell(null);
    setFeedback(null);
    setShowHint(false);
    setHintsUsed(0);
    setCompletionTime(0);
    setStartTime(Date.now());
    setTimeElapsed(0);
    setHasSubmitted(false);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const shareResult = () => {
    const hintsText = hintsUsed > 0 ? `💡 Used ${hintsUsed}/${maxHints} hints` : '💡 No hints used';
    const shareText = `Daily Combined Grid ${getTodayDateString()} ➕✖️\\n\\n⏱️ Time: ${formatTime(completionTime)}\\n${hintsText}\\n🔢 Satisfy both sum AND product targets!\\n\\nTry today's puzzle at app.eightythreelabs.com/combined-grid`;
    
    if (navigator.share) {
      navigator.share({
        title: 'Daily Combined Grid Results',
        text: shareText,
      });
    } else {
      navigator.clipboard.writeText(shareText);
      alert('Results copied to clipboard!');
    }
  };


  // Show loading state
  if (checkingCompletion) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  // Show "come back tomorrow" for completed daily challenges
  if (!isPracticeMode && hasCompletedToday) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-900 dark:to-gray-800 p-4">
        {/* Header */}
        <div className="max-w-4xl mx-auto mb-6">
          <div className="flex items-center justify-between mb-4">
            <Link to="/" className="text-blue-600 hover:text-blue-800 font-semibold">
              ← Back to Games
            </Link>
          </div>
        </div>

        {/* Come Back Tomorrow Message */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8 text-center">
            <div className="text-6xl mb-6">✅</div>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-4">
              Daily Challenge Complete!
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              You've already completed today's Combined Grid challenge. Come back tomorrow for a new puzzle!
            </p>
            
            {/* Countdown Timer */}
            <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
                ⏰ Next Challenge In:
              </h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-white dark:bg-gray-700 rounded-lg p-3">
                  <div className="text-2xl font-bold text-orange-600">{timeUntilNext.hours}</div>
                  <div className="text-sm text-gray-600">Hours</div>
                </div>
                <div className="bg-white dark:bg-gray-700 rounded-lg p-3">
                  <div className="text-2xl font-bold text-orange-600">{timeUntilNext.minutes}</div>
                  <div className="text-sm text-gray-600">Minutes</div>
                </div>
                <div className="bg-white dark:bg-gray-700 rounded-lg p-3">
                  <div className="text-2xl font-bold text-orange-600">{timeUntilNext.seconds}</div>
                  <div className="text-sm text-gray-600">Seconds</div>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                Puzzles reset at Midnight EST
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Link
                to="/combined-grid?mode=practice"
                className="block w-full bg-orange-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
              >
                🎮 Play Practice Mode
              </Link>
              <Link
                to="/puzzle-games"
                className="block w-full bg-gray-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-600 transition-colors"
              >
                🧩 Try Other Puzzle Games
              </Link>
              <Link
                to="/"
                className="block w-full bg-green-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-600 transition-colors"
              >
                🏠 Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!puzzle) {
    return <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
      <div className="text-2xl">Loading puzzle...</div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-900 dark:to-gray-800 p-4">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="flex items-center justify-between mb-4">
          <Link to="/" className="text-blue-600 hover:text-blue-800 font-semibold">
            ← Back to Games
          </Link>
          <div className="text-lg font-semibold text-gray-700">
            Time: {formatTime(timeElapsed)}
          </div>
        </div>
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
            {isPracticeMode ? "Combined Grid - Practice Mode" : `Daily Combined Grid - ${getTodayDateString()}`}
          </h1>
          <p className="text-gray-600">
            Fill the 3×3 grid so rows and columns satisfy BOTH sum and product targets
          </p>
          <p className="text-sm text-orange-600 mt-2">
            {isPracticeMode 
              ? "🎮 Unlimited practice rounds with the same rules as daily challenges!"
              : "🗓️ Everyone gets the same puzzle today! Compare your times with friends."
            }
          </p>
        </div>
      </div>

      {/* Game Complete Modal */}
      {gameComplete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 max-w-lg mx-4 text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Puzzle Complete!</h2>
            <p className="text-gray-600 mb-6">
              Amazing! You solved the combined puzzle in {formatTime(completionTime)}.
            </p>
            
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-orange-600">{formatTime(completionTime)}</div>
                  <div className="text-sm text-gray-600">Completion Time</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-600">{hintsUsed}/{maxHints}</div>
                  <div className="text-sm text-gray-600">Hints Used</div>
                </div>
              </div>
            </div>
            
            {/* Signup prompt for non-authenticated users who completed daily challenge */}
            {!isAuthenticated && !isPracticeMode && (
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 mb-4 border border-green-200">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">🏆 Save Your Daily Challenge Score!</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Create an account to save your daily challenge progress and track your improvement over time.
                </p>
                <div className="space-y-2">
                  <Link
                    to="/signup"
                    className="block w-full bg-green-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-green-700 transition-colors text-sm"
                  >
                    🎯 Create Account & Save Score
                  </Link>
                  <Link
                    to="/login"
                    className="block w-full bg-blue-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-sm"
                  >
                    🔑 Sign In to Save Score
                  </Link>
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              <button
                onClick={shareResult}
                className="w-full bg-blue-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
              >
                📱 Share Results
              </button>
              
              {!isPracticeMode ? (
                // Daily puzzle completion - show different options
                <div className="space-y-3">
                  
                  <button
                    onClick={startNewPuzzle}
                    className="w-full bg-orange-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
                  >
                    🎮 Try Again
                  </button>
                  
                  <Link
                    to="/"
                    className="block w-full bg-gray-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-600 transition-colors text-center"
                  >
                    🏠 Go Back to Dashboard
                  </Link>
                </div>
              ) : (
                // Practice mode - just show new puzzle option
                <button
                  onClick={startNewPuzzle}
                  className="w-full bg-orange-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
                >
                  🎮 New Practice Puzzle
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Signup Prompt Modal */}
      {showSignupPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 max-w-md mx-4 text-center">
            <div className="text-4xl mb-4">🚀</div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Want Unlimited Puzzles?</h2>
            <p className="text-gray-600 mb-6">
              You've completed today's daily puzzle! Sign up for free to access unlimited puzzles and track your progress.
            </p>
            
            <div className="space-y-3">
              <Link
                to="/signup"
                className="block w-full bg-blue-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
              >
                🎯 Sign Up for Free
              </Link>
              
              <Link
                to="/login"
                className="block w-full bg-gray-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-600 transition-colors"
              >
                Already have an account? Sign In
              </Link>
              
              <button
                onClick={() => setShowSignupPrompt(false)}
                className="w-full bg-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Game Layout */}
      <div className="max-w-4xl mx-auto px-4">

        {/* Game Board */}
        <div className="mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8">
              
              {/* Grid */}
              <div className="flex justify-center mb-8">
                <div className="inline-block">
                  {/* Column targets header */}
                  <div className="flex mb-4">
                    <div className="w-20"></div>
                    {puzzle.colSums.map((sum, col) => (
                      <div key={col} className="w-16 h-16 flex flex-col items-center justify-center">
                        <span className="text-red-600 font-bold text-sm">+{sum}</span>
                        <span className="text-purple-600 font-bold text-sm">×{puzzle.colProducts[col]}</span>
                      </div>
                    ))}
                  </div>

                  {/* Grid with row targets */}
                  {puzzle.grid.map((row, rowIndex) => (
                    <div key={rowIndex} className="flex">
                      <div className="w-20 h-16 flex flex-col items-center justify-center">
                        <span className="text-red-600 font-bold text-sm">+{puzzle.rowSums[rowIndex]}</span>
                        <span className="text-purple-600 font-bold text-sm">×{puzzle.rowProducts[rowIndex]}</span>
                      </div>
                      
                      {row.map((cell, colIndex) => {
                        const isSelected = selectedCell?.row === rowIndex && selectedCell?.col === colIndex;
                        
                        const cellBgColor = cell.isGiven 
                          ? 'bg-blue-100'
                          : hasSubmitted && cell.value !== null && cell.isValid 
                          ? 'bg-green-500'
                          : hasSubmitted && cell.value !== null && !cell.isValid 
                          ? 'bg-red-500'
                          : isSelected 
                          ? 'bg-yellow-100'
                          : 'bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600';
                        
                        const textColor = cell.isGiven 
                          ? 'text-blue-800'
                          : hasSubmitted && cell.value !== null && (cell.isValid || !cell.isValid)
                          ? 'text-white'
                          : 'text-gray-800 dark:text-gray-100';
                        
                        return (
                          <div
                            key={colIndex}
                            onClick={() => handleCellClick(rowIndex, colIndex)}
                            className={`
                              w-16 h-16 border-2 border-gray-800 flex items-center justify-center text-2xl font-bold cursor-pointer transition-all duration-200
                              ${cellBgColor} ${textColor}
                            `}
                          >
                            {cell.value || ''}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

              {/* Number Input Buttons */}
              <div className="flex justify-center mb-6">
                <div className="grid grid-cols-5 gap-3">
                  {Array.from({length: 9}, (_, i) => i + 1).map(num => {
                    const usedNumbers = getUsedNumbers();
                    const selectedCellValue = selectedCell ? puzzle.grid[selectedCell.row][selectedCell.col].value : null;
                    const isNumberUsed = usedNumbers.has(num);
                    const canUseNumber = !isNumberUsed || selectedCellValue === num;
                    const isDisabled = !selectedCell || 
                                     puzzle.grid[selectedCell.row][selectedCell.col].isGiven || 
                                     !canUseNumber;
                    
                    return (
                      <button
                        key={num}
                        onClick={() => handleNumberInput(num)}
                        disabled={isDisabled}
                        className={`w-14 h-14 rounded-lg font-bold text-xl transition-colors ${
                          isDisabled 
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                            : isNumberUsed 
                            ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                            : 'bg-orange-500 text-white hover:bg-orange-600'
                        }`}
                      >
                        {num}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => {
                      if (!puzzle || !selectedCell) return;
                      const newGrid = puzzle.grid.map((row, r) =>
                        row.map((cell, c) => {
                          if (r === selectedCell.row && c === selectedCell.col && !cell.isGiven) {
                            return { ...cell, value: null, isValid: true };
                          }
                          return cell;
                        })
                      );
                      setPuzzle({...puzzle, grid: newGrid});
                      setFeedback(null);
                      setHasSubmitted(false);
                    }}
                    disabled={!selectedCell || puzzle.grid[selectedCell.row][selectedCell.col].isGiven}
                    className="w-14 h-14 bg-red-500 text-white rounded-lg font-bold text-xl hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-4 mb-6">
                {isPracticeMode ? (
                  <>
                    {/* Practice Mode: Full button layout */}
                    {/* Row 1: Helper/Tool Buttons */}
                    <div className="flex justify-center gap-4">
                      <button
                        onClick={handleGetHint}
                        disabled={hintsUsed >= maxHints}
                        className="px-6 py-3 bg-yellow-500 text-white rounded-lg font-bold text-lg hover:bg-yellow-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                      >
                        💡 Get Hint ({maxHints - hintsUsed} left)
                      </button>
                      <button
                        onClick={() => setShowWhiteboard(true)}
                        className="px-6 py-3 bg-blue-500 text-white rounded-lg font-bold text-lg hover:bg-blue-600 transition-colors"
                      >
                        📝 Scratchpad
                      </button>
                    </div>
                    
                    {/* Row 2: Main Action Buttons */}
                    <div className="flex justify-center gap-4">
                      <button
                        onClick={clearPuzzle}
                        className="px-6 py-3 bg-red-500 text-white rounded-lg font-bold text-lg hover:bg-red-600 transition-colors"
                      >
                        🗑️ Clear Puzzle
                      </button>
                      <button
                        onClick={startNewPuzzle}
                        className="px-6 py-3 bg-gray-500 text-white rounded-lg font-bold text-lg hover:bg-gray-600 transition-colors"
                      >
                        🎮 New Puzzle
                      </button>
                      <button
                        onClick={handleSubmit}
                        disabled={!isGridComplete()}
                        className="px-8 py-3 bg-green-600 text-white rounded-lg font-bold text-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors shadow-lg"
                      >
                        ✅ Submit
                      </button>
                    </div>
                  </>
                ) : (
                  /* Daily Challenge Mode: Only 3 buttons */
                  <div className="flex justify-center gap-4">
                    <button
                      onClick={clearPuzzle}
                      className="px-6 py-3 bg-red-500 text-white rounded-lg font-bold text-lg hover:bg-red-600 transition-colors"
                    >
                      🗑️ Clear Puzzle
                    </button>
                    <button
                      onClick={() => setShowWhiteboard(true)}
                      className="px-6 py-3 bg-blue-500 text-white rounded-lg font-bold text-lg hover:bg-blue-600 transition-colors"
                    >
                      📝 Scratchpad
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={!isGridComplete()}
                      className="px-8 py-3 bg-green-600 text-white rounded-lg font-bold text-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors shadow-lg"
                    >
                      ✅ Submit
                    </button>
                  </div>
                )}
              </div>

              {/* Feedback Messages */}
              {feedback && (
                <div className={`text-center mb-6 p-4 rounded-lg font-bold text-lg ${
                  feedback === 'correct' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {feedback === 'correct' 
                    ? '🎉 Excellent! All constraints satisfied!' 
                    : '❌ Some constraints not met. Check the red squares!'}
                </div>
              )}

              {showHint && (
                <div className="text-center mb-6 p-4 rounded-lg bg-yellow-100 text-yellow-800 font-bold text-lg">
                  💡 Hint added! Blue squares show the correct answers.
                </div>
              )}

              {/* Instructions */}
              <div className="text-center text-gray-600">
                <p className="mb-2">
                  <strong>How to play:</strong> Click a cell, then click a number to place it.
                </p>
                <p className="mb-2">
                  Use each number 1-9 exactly once. Make rows and columns satisfy BOTH sum and product targets.
                </p>
                <p className="text-sm mb-2">
                  <span className="text-red-600 font-semibold">Red numbers</span>: Addition targets • 
                  <span className="text-purple-600 font-semibold"> Purple numbers</span>: Multiplication targets
                </p>
                <p className="text-sm">
                  🟦 Blue buttons: Available numbers • 🟨 Yellow buttons: Used numbers (click to remove) • ⚫ Gray buttons: Disabled
                </p>
              </div>
            </div>
        </div>
      </div>
      
      {/* Whiteboard Component */}
      <Whiteboard 
        isOpen={showWhiteboard} 
        onClose={() => setShowWhiteboard(false)} 
      />
    </div>
  );
};

export default CombinedGrid;